import React from 'react';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { BookOpen, Users, HeadphonesIcon, CheckCircle, ExternalLink } from 'lucide-react';
export const TeachersProgram = () => {
  return <div className="pt-20" data-id="element-651">
      {/* Hero */}
      <div className="bg-[#F2F0E7] py-20 md:py-32" data-id="element-652">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-653">
          <div className="max-w-3xl" data-id="element-654">
            <div className="inline-block px-4 py-2 bg-white text-[#FC963E] rounded-full font-semibold text-sm mb-6 shadow-sm border-2 border-[#FC963E]/20" data-id="element-655">
              For Educators
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-[#1C274C] mb-6 leading-tight" data-id="element-656">
              Project AI for <span className="text-[#FC963E]" data-id="element-657">Educators</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-658">
              A capacity-building initiative designed to help teachers
              confidently understand and integrate Artificial Intelligence into
              education, responsibly and ethically.
            </p>
            <p className="text-lg text-gray-600 mb-8" data-id="element-659">
              Educators play a critical role in shaping how children interact
              with technology. This program ensures they are prepared,
              supported, and empowered — not left behind.
            </p>
            <div className="flex gap-4" data-id="element-660">
              <a href="#waitlist-form" onClick={e => {
              e.preventDefault();
              document.getElementById('waitlist-form')?.scrollIntoView({
                behavior: 'smooth'
              });
            }} data-id="element-661">
                <Button variant="secondary" size="lg" data-id="element-662">
                  Join the Waitlist
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* What the Program Offers */}
      <Section data-id="element-663">
        <div className="max-w-4xl mx-auto" data-id="element-664">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1C274C] mb-12 text-center" data-id="element-665">
            What the Program Offers
          </h2>

          <div className="space-y-8" data-id="element-666">
            <div className="bg-white p-8 rounded-2xl border-2 border-[#FC963E]/20 shadow-sm" data-id="element-667">
              <div className="flex items-start gap-4" data-id="element-668">
                <div className="w-12 h-12 bg-[#FC963E]/10 rounded-full flex items-center justify-center shrink-0" data-id="element-669">
                  <Users className="w-6 h-6 text-[#FC963E]" data-id="element-670" />
                </div>
                <div data-id="element-671">
                  <h3 className="text-xl font-bold text-[#1C274C] mb-3" data-id="element-672">
                    AI for Educators Seminar
                  </h3>
                  <p className="text-gray-600 mb-3" data-id="element-673">
                    A beginner-friendly session introducing:
                  </p>
                  <ul className="space-y-2 text-gray-600" data-id="element-674">
                    <li className="flex items-start" data-id="element-675">
                      <span className="text-[#FC963E] mr-2" data-id="element-676">•</span>
                      What AI is (and what it isn't)
                    </li>
                    <li className="flex items-start" data-id="element-677">
                      <span className="text-[#FC963E] mr-2" data-id="element-678">•</span>
                      Practical AI tools for teaching tasks
                    </li>
                    <li className="flex items-start" data-id="element-679">
                      <span className="text-[#FC963E] mr-2" data-id="element-680">•</span>
                      Ethical considerations in classroom use
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl border-2 border-[#3AB5E8]/20 shadow-sm" data-id="element-681">
              <div className="flex items-start gap-4" data-id="element-682">
                <div className="w-12 h-12 bg-[#3AB5E8]/10 rounded-full flex items-center justify-center shrink-0" data-id="element-683">
                  <BookOpen className="w-6 h-6 text-[#3AB5E8]" data-id="element-684" />
                </div>
                <div data-id="element-685">
                  <h3 className="text-xl font-bold text-[#1C274C] mb-2" data-id="element-686">
                    AI for Educators Book Kit
                  </h3>
                  <p className="text-gray-600" data-id="element-687">
                    A simple, practical resource for continued learning and
                    experimentation.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl border-2 border-[#389C70]/20 shadow-sm" data-id="element-688">
              <div className="flex items-start gap-4" data-id="element-689">
                <div className="w-12 h-12 bg-[#389C70]/10 rounded-full flex items-center justify-center shrink-0" data-id="element-690">
                  <HeadphonesIcon className="w-6 h-6 text-[#389C70]" data-id="element-691" />
                </div>
                <div data-id="element-692">
                  <h3 className="text-xl font-bold text-[#1C274C] mb-2" data-id="element-693">
                    Post-Training Technical Support
                  </h3>
                  <p className="text-gray-600" data-id="element-694">
                    Ongoing guidance to help educators apply what they've
                    learned and ask questions as they explore AI tools.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Who It's For */}
      <Section background="gray" data-id="element-695">
        <div className="max-w-4xl mx-auto text-center" data-id="element-696">
          <h2 className="text-3xl font-bold text-[#1C274C] mb-6" data-id="element-697">
            Who It's For
          </h2>
          <div className="bg-white p-8 rounded-2xl shadow-sm inline-block" data-id="element-698">
            <ul className="text-left space-y-3 text-lg text-gray-700" data-id="element-699">
              <li className="flex items-center" data-id="element-700">
                <CheckCircle className="w-5 h-5 text-[#389C70] mr-3 shrink-0" data-id="element-701" />
                Primary and secondary school teachers
              </li>
              <li className="flex items-center" data-id="element-702">
                <CheckCircle className="w-5 h-5 text-[#389C70] mr-3 shrink-0" data-id="element-703" />
                School administrators and education staff
              </li>
            </ul>
          </div>
        </div>
      </Section>

      {/* Why It Matters */}
      <Section data-id="element-704">
        <div className="max-w-4xl mx-auto" data-id="element-705">
          <h2 className="text-3xl font-bold text-[#1C274C] mb-6" data-id="element-706">
            Why It Matters
          </h2>
          <p className="text-lg text-gray-600 mb-6" data-id="element-707">
            When educators understand AI, they can:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-708">
            {['Guide students responsibly', 'Reduce fear and misinformation', 'Improve teaching efficiency', 'Protect children from misuse'].map((item, index) => <div key={index} className="flex items-start bg-[#F2F0E7] p-4 rounded-lg" data-id="element-709">
                <span className="text-[#FC963E] mr-3 text-xl" data-id="element-710">✓</span>
                <span className="text-gray-700 font-medium" data-id="element-711">{item}</span>
              </div>)}
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section className="bg-[#FC963E] text-white text-center" id="waitlist-form" data-id="element-712">
        <h2 className="text-3xl font-bold mb-6" data-id="element-713">
          Ready to empower your teaching with AI?
        </h2>
        <p className="text-xl mb-10 max-w-2xl mx-auto" data-id="element-714">
          Join our waitlist and be among the first educators to participate in
          this transformative training program.
        </p>
        <div className="max-w-2xl mx-auto rounded-2xl overflow-hidden shadow-2xl" data-id="element-715">
          <iframe src="https://docs.google.com/forms/d/e/1FAIpQLScSGJW6JChNdbjYMdQzoXgD0Vd68Rzf2msL-nlGLYEjPYnZpg/viewform?embedded=true" width="100%" height="800" frameBorder="0" marginHeight={0} marginWidth={0} className="bg-white" data-id="element-716">
            Loading…
          </iframe>
        </div>
      </Section>
    </div>;
};